// Full-screen upload -> OCR -> compare/merge flow, entered as soon as a file
// is picked (mirrors AnalyzeOrder's "Add transport order" step: pick a file,
// go straight to full-screen, show processing steps there, then the form).
// Layout follows AnalyzeOrderVerify.tsx: resizable left panel for the
// merge form, right panel is the uploaded file in the same /viewers/web
// iframe viewer once it's available.

import { RefObject, useState } from "react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { AlertCircleIcon, ArrowLeft, Loader2 } from "lucide-react"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { RotatingTextShimmer } from "@/components/ui/rotating-text-shimmer"
import CompareFieldsList from "@/components/maps/orderDetails/CompareFieldsList"
import {
  buildCompareFields,
  buildInitialResolution,
  CompareFieldKey,
  ResolutionState,
} from "@/components/maps/orderDetails/compareFields"
import { RetrofitResult } from "@/components/maps/orderDetails/useOriginalDocumentUpload"

const UploadFlowFullScreen = ({
  fileInputRef,
  onFileInputChange,
  uploading,
  error,
  result,
  onRetryFile,
  onDiscard,
  onSaved,
}: {
  fileInputRef: RefObject<HTMLInputElement>
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  uploading: boolean
  error: string | null
  result: RetrofitResult | null
  onRetryFile: () => void
  onDiscard: () => void
  onSaved: (resolution: ResolutionState) => Promise<void>
}) => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const title = uploading
    ? t("orders.orderDetails.upload.analyzingTitle")
    : error
      ? t("orders.orderDetails.upload.failedTitle")
      : t("orders.orderDetails.upload.reviewTitle")

  // Same offset AnalyzeOrderVerify.tsx uses so the fixed overlay doesn't
  // cover the app's own left icon-sidebar in the real product shell.
  const inProduct = Boolean(window.XMPP && !window.anonymousXMPP)

  return (
    <div
      className={`${inProduct ? "left-[49px] w-[calc(100vw-3rem)]" : ""} fixed inset-0 z-[100] h-screen w-screen bg-white dark:bg-gray-900`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={onFileInputChange}
      />
      <ResizablePanelGroup direction="horizontal" className="min-h-screen">
        <ResizablePanel
          className="relative flex flex-col bg-sidebar"
          defaultSize={40}
          maxSize={55}
          minSize={25}
        >
          <div className="flex w-full flex-col p-4 pb-2">
            <Button
              variant="ghost"
              onClick={onDiscard}
              disabled={uploading}
              className="self-start pl-0 text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("common.back")}
            </Button>
            <h1 className="mt-4 text-2xl font-bold">{title}</h1>
          </div>
          <div className="flex-1 overflow-y-auto p-4 pt-0">
            {uploading && (
              <RotatingTextShimmer
                messages={[
                  t("orders.orderDetails.upload.steps.uploading"),
                  t("orders.orderDetails.upload.steps.ocr"),
                  t("orders.orderDetails.upload.steps.extracting"),
                ]}
              />
            )}

            {!uploading && error && (
              <Alert variant="destructive">
                <AlertCircleIcon />
                <AlertTitle>
                  {t("orders.orderDetails.upload.unableToProcess")}
                </AlertTitle>
                <AlertDescription>
                  <p>{error}</p>
                  <Button
                    className="mt-2"
                    variant="outline"
                    onClick={onRetryFile}
                  >
                    {t("orders.orderDetails.upload.tryAgain")}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {!uploading && !error && result && (
              <MergeReview
                result={result}
                onSaved={onSaved}
                setSaving={setSaving}
              />
            )}
          </div>
          {!uploading && !error && result && (
            <div className="flex justify-end gap-2 border-t p-4">
              <Button variant="outline" onClick={onDiscard} disabled={saving}>
                {t("common.cancel")}
              </Button>
              <Button form="merge-review-form" type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("common.save")}
              </Button>
            </div>
          )}
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel
          defaultSize={60}
          className="flex flex-col bg-[hsl(var(--muted-foreground))]"
        >
          <div className="flex items-center justify-center bg-black/20 py-1.5 text-xs font-medium text-white">
            {t("orders.orderDetails.upload.uploadedDocument")}
          </div>
          {result ? (
            <iframe
              className="h-full w-full"
              src={`/viewers/web/${result.fileUrl.toLowerCase().endsWith(".pdf") ? "viewer.html" : "imageViewer.html"}?url=${encodeURIComponent(result.fileUrl)}`}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-white/80">
              {t("orders.orderDetails.upload.preparingPreview")}
            </div>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}

// Owns the resolution state for the compare step — mounts only once a
// result exists, so (unlike the parent) it can lazy-init straight from
// props. Submits via a named <form> so its own "Save merge" button can live
// in the panel footer above, outside this subtree.
const MergeReview = ({
  result,
  onSaved,
  setSaving,
}: {
  result: RetrofitResult
  onSaved: (resolution: ResolutionState) => Promise<void>
  setSaving: (saving: boolean) => void
}) => {
  const { t } = useTranslation()
  const fields = buildCompareFields(result.existingFields, result.extracted)
  const [resolution, setResolution] = useState(() =>
    buildInitialResolution(fields),
  )

  const onPick = (key: CompareFieldKey, source: "old" | "new") => {
    const field = fields.find((f) => f.key === key)!
    setResolution((prev) => ({
      ...prev,
      [key]: {
        source,
        value: source === "old" ? field.oldValue : field.newValue,
      },
    }))
  }

  const onManualEdit = (key: CompareFieldKey, value: string) => {
    setResolution((prev) => ({ ...prev, [key]: { source: "manual", value } }))
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await onSaved(resolution)
      // On success the parent closes this flow (resetUpload), unmounting
      // this component — nothing left to reset here.
    } catch (err: any) {
      setSaving(false)
      toast.error(err?.message ?? t("orders.orderDetails.saveFailed"))
    }
  }

  return (
    <form id="merge-review-form" onSubmit={onSubmit}>
      <CompareFieldsList
        fields={fields}
        resolution={resolution}
        onPick={onPick}
        onManualEdit={onManualEdit}
      />
    </form>
  )
}

export default UploadFlowFullScreen
